import React from "react";
import { connect } from "react-redux";
import Grid from "@material-ui/core/Grid";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { makeStyles } from "@material-ui/styles";
import SkeletonWrapper from "../../../containers/NewProductList/components/SkeletonWrapper/SkeletonWrapper";
import AlertDialog from "../../../components/AlertDialog/index.es6";
import CancellationTable from "./Components/CancellationTable";
import CancellationActionDialog from "./Components/CancellationActionDialog";
import {
  getPolicyCancellationRequestsAction,
  updatePolicyCancellationAction,
  resetUpdatePolicyCancellationSuccess,
  resetUpdatePolicyCancellationError,
} from "./Redux/actions";

const useStyles = makeStyles(() => ({
  header: {
    background: "#FFFFFF",
    borderRadius: "8px",
  },
  title: {
    marginLeft: "20px",
    "& h1": {
      fontFamily: "Lato",
      fontStyle: "normal",
      fontWeight: 500,
      fontSize: "16px",
      lineHeight: "24px",
      letterSpacing: "0.0015em",
      color: "#000000",
    },
  },
  wrapper: {
    marginTop: "30px",
    "& button": {
      fontFamily: "Lato",
      fontStyle: "normal",
      fontWeight: 400,
      fontSize: "14px",
      lineHeight: "20px",
      letterSpacing: "0.001em",
      color: "#1E1E1E",
      textTransform: "capitalize",
    },
  },
  tabs: {
    "& .MuiTabs-indicator": {
      backgroundColor: "#032A37",
    },
    "& .MuiTab-root.Mui-selected": {
      color: "#fff",
      background: "#032A37",
      borderRadius: "8px 8px 0px 0px",
    },
  },
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`cancellation-tabpanel-${index}`}
      aria-labelledby={`cancellation-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `cancellation-tab-${index}`,
    "aria-controls": `cancellation-tabpanel-${index}`,
  };
}

const STATUSES = ["Raised", "Approved", "Rejected"];

class PolicyCancellationDashboard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: 0,
      actionDialogOpen: false,
      actionType: null,
      selectedPolicy: null,
    };
  }

  componentWillMount() {
    this.fetchCancellationRequests({ status: STATUSES[0] });
  }

  fetchCancellationRequests(payload = {}) {
    const status = payload.status || STATUSES[this.state.activeTab];
    this.props.dispatch(
      getPolicyCancellationRequestsAction({
        ...payload,
        status,
        page: payload.page || 1,
        size: payload.size || 10,
      })
    );
  }

  handleTabChange = (event, newValue) => {
    this.setState({ activeTab: newValue });
    this.fetchCancellationRequests({ status: STATUSES[newValue] });
  };

  handleApprove = (policy) => {
    this.setState({
      actionDialogOpen: true,
      actionType: "Approved",
      selectedPolicy: policy,
    });
  };

  handleReject = (policy) => {
    this.setState({
      actionDialogOpen: true,
      actionType: "Rejected",
      selectedPolicy: policy,
    });
  };

  handleActionConfirm = (reason) => {
    const { selectedPolicy, actionType } = this.state;
    this.props.dispatch(
      updatePolicyCancellationAction({
        policy_guid: selectedPolicy.guid || selectedPolicy.policy_guid,
        status: actionType,
        reason,
        additional_details: selectedPolicy.additional_details || {},
      })
    );
    this.setState({ actionDialogOpen: false, actionType: null, selectedPolicy: null });
  };

  handleActionDialogClose = () => {
    this.setState({ actionDialogOpen: false, actionType: null, selectedPolicy: null });
  };

  componentDidUpdate(prevProps) {
    if (
      this.props.policyCancellationData.updateSuccess &&
      !prevProps.policyCancellationData.updateSuccess
    ) {
      // Refresh current tab after successful update
      this.fetchCancellationRequests({ status: STATUSES[this.state.activeTab] });
    }
  }

  render() {
    const { policyCancellationData } = this.props;
    const { activeTab, actionDialogOpen, actionType } = this.state;

    return (
      <PolicyCancellationUI
        activeTab={activeTab}
        onTabChange={this.handleTabChange}
        data={policyCancellationData}
        onPageChange={(payload) => this.fetchCancellationRequests(payload)}
        onApprove={this.handleApprove}
        onReject={this.handleReject}
        actionDialogOpen={actionDialogOpen}
        actionType={actionType}
        onActionConfirm={this.handleActionConfirm}
        onActionDialogClose={this.handleActionDialogClose}
        onResetUpdateSuccess={() =>
          this.props.dispatch(resetUpdatePolicyCancellationSuccess())
        }
        onResetUpdateError={() =>
          this.props.dispatch(resetUpdatePolicyCancellationError())
        }
      />
    );
  }
}

const PolicyCancellationUI = (props) => {
  const classes = useStyles();
  const {
    activeTab,
    onTabChange,
    data,
    onPageChange,
    onApprove,
    onReject,
    actionDialogOpen,
    actionType,
    onActionConfirm,
    onActionDialogClose,
    onResetUpdateSuccess,
    onResetUpdateError,
  } = props;

  if (data.loader) {
    return <SkeletonWrapper loading={data.loader} />;
  }

  return (
    <>
      <section className={classes.header}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12}>
            <div className={classes.title}>
              <h1>Policy Cancellation Requests</h1>
            </div>
          </Grid>
        </Grid>
      </section>

      <section className={classes.wrapper}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={activeTab}
                onChange={onTabChange}
                aria-label="policy cancellation tabs"
                variant="scrollable"
                scrollButtons="auto"
                className={classes.tabs}
              >
                <Tab label="Raised" {...a11yProps(0)} />
                <Tab label="Approved" {...a11yProps(1)} />
                <Tab label="Rejected" {...a11yProps(2)} />
              </Tabs>
            </Box>

            <TabPanel value={activeTab} index={0}>
              <CancellationTable
                policies={data.cancellationRequests}
                loader={data.loader}
                page={data.page}
                size={data.size}
                total={data.total}
                status="Raised"
                onPageChange={onPageChange}
                onApprove={onApprove}
                onReject={onReject}
              />
            </TabPanel>
            <TabPanel value={activeTab} index={1}>
              <CancellationTable
                policies={data.cancellationRequests}
                loader={data.loader}
                page={data.page}
                size={data.size}
                total={data.total}
                status="Approved"
                onPageChange={onPageChange}
              />
            </TabPanel>
            <TabPanel value={activeTab} index={2}>
              <CancellationTable
                policies={data.cancellationRequests}
                loader={data.loader}
                page={data.page}
                size={data.size}
                total={data.total}
                status="Rejected"
                onPageChange={onPageChange}
              />
            </TabPanel>
          </Grid>
        </Grid>
      </section>

      <CancellationActionDialog
        open={actionDialogOpen}
        actionType={actionType}
        onClose={onActionDialogClose}
        onConfirm={onActionConfirm}
        loading={data.updateLoader}
      />

      <AlertDialog
        success
        show={data.updateSuccess}
        title="Cancellation request updated successfully"
        onConfirm={onResetUpdateSuccess}
        confirmBtnText="OK"
        style={{ display: "block", marginTop: "80px", fontSize: "100%", maxWidth: "500px", margin: "80px auto" }}
      />

      <AlertDialog
        danger
        show={!!data.updateError}
        title="Failed to update cancellation request"
        onConfirm={onResetUpdateError}
        confirmBtnText="OK"
        style={{ display: "block", marginTop: "80px", fontSize: "100%", maxWidth: "500px", margin: "80px auto" }}
      />
    </>
  );
};

export default connect((state) => ({
  policyCancellationData: state.policyCancellationData,
}))(PolicyCancellationDashboard);
